import React, { useMemo } from 'react';
import {
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonIcon,
    IonContent,
    IonItem,
    IonChip,
    IonLabel,
    IonList,
    IonPage,
    IonProgressBar
} from '@ionic/react';
import { wifi, unlinkOutline, closeOutline, refreshOutline } from 'ionicons/icons';
import { LineChart } from '@mui/x-charts/LineChart';
import Timestamp from '../components/Timestamp';
import { useTranslation } from '../hooks/useTranslation';
import { useDeviceQuery } from '../query/deviceQuery';
import { useDeviceRebootLogsQuery } from '../query/deviceRebootLogsQuery';
import { useDeviceTelemetryMetricsQuery } from '../query/deviceTelemetryMetricsQuery';
import { useDeveloperModeQuery } from '../query/developerModeQuery';
import { Device as DeviceModel } from '../models/Device';
import SectionHeader from '../components/SectionHeader';
import sectionStyles from '../components/SectionHeader.module.css';

interface TelemetryMetricConfig {
    id: string;
    label: string;
    measureName: string;
    color: string;
    unit?: string;
    transform?: (value: number) => number;
    formatValue?: (value: number) => string;
}

type TelemetryChartSeries = Omit<TelemetryMetricConfig, 'measureName' | 'transform'> & {
    data: number[];
    timeline: Date[];
};

interface NormalizedMetricPoint {
    timestamp: Date;
    value: number;
}

const BYTES_IN_GB = 1000 ** 3;
const BYTES_IN_MB = 1024 ** 2;
const SECONDS_IN_HOUR = 3600;
const AREA_GRADIENT_TOP_OPACITY = 0.32;
const WIFI_RSSI_THRESHOLDS = {
    excellent: -30,
    good: -50,
    medium: -67,
    poor: -80
};
const WIFI_CHART_HEIGHT = 120;
const WIFI_CHART_MARGIN = {
    top: 12,
    bottom: 8,
    left: 4,
    right: 4
};
const WIFI_RSSI_COLORS = {
    excellent: '#1b5e20',
    good: '#2e7d32',
    medium: '#f9a825',
    poor: '#e53935',
    line: '#455a64'
};

const buildTemperatureMetrics = (t: (key: string) => string): TelemetryMetricConfig[] => ([
    {
        id: 'tcpu',
        label: t('device_health_modal.metrics.soc_temperature'),
        measureName: 'tcpu',
        color: '#ef5350',
        unit: '°C',
        formatValue: (value: number) => `${value.toFixed(1)}°C`
    },
    {
        id: 'tdie',
        label: t('device_health_modal.metrics.pmic_temperature'),
        measureName: 'tdie',
        color: '#42a5f5',
        unit: '°C',
        formatValue: (value: number) => `${value.toFixed(1)}°C`
    },
    {
        id: 'tbat',
        label: t('device_health_modal.metrics.battery_temperature'),
        measureName: 'tbat',
        color: '#66bb6a',
        unit: '°C',
        formatValue: (value: number) => `${value.toFixed(1)}°C`
    }
]);

const buildPowerMetrics = (t: (key: string) => string): TelemetryMetricConfig[] => ([
    {
        id: 'ibus',
        label: t('device_health_modal.metrics.input_current'),
        measureName: 'ibus',
        color: '#7e57c2',
        unit: 'mA',
        transform: (value: number) => value * 1000,
        formatValue: (value: number) => `${value.toFixed(0)} mA`
    },
    {
        id: 'ibat',
        label: t('device_health_modal.metrics.battery_charge_current'),
        measureName: 'ibat',
        color: '#26c6da',
        unit: 'mA',
        transform: (value: number) => value * 1000,
        formatValue: (value: number) => `${value.toFixed(0)} mA`
    },
    {
        id: 'vbus',
        label: t('device_health_modal.metrics.power_supply'),
        measureName: 'vbus',
        color: '#ff7043',
        formatValue: (value: number) => `${(value >= 10 ? value.toFixed(0) : value.toFixed(2))} V`
    },
    {
        id: 'vbat',
        label: t('device_health_modal.metrics.battery_voltage'),
        measureName: 'vbat',
        color: '#66bb6a',
        formatValue: (value: number) => `${value.toFixed(2)} V`
    },
    {
        id: 'vcc',
        label: t('device_health_modal.metrics.soc_voltage'),
        measureName: 'vcc',
        color: '#ffa726',
        formatValue: (value: number) => `${value.toFixed(2)} V`
    },
    {
        id: 'vsys',
        label: t('device_health_modal.metrics.system_voltage'),
        measureName: 'vsys',
        color: '#ab47bc',
        formatValue: (value: number) => `${value.toFixed(2)} V`
    }
]);

const buildStorageMetrics = (t: (key: string) => string): TelemetryMetricConfig[] => ([
    {
        id: 'free-storage',
        label: t('device_health_modal.metrics.free_storage'),
        measureName: 'freestorage',
        unit: 'GB',
        color: '#26a69a',
        transform: (value: number) => value / BYTES_IN_GB,
        formatValue: (value: number) => `${value.toFixed(2)} GB`
    },
    {
        id: 'free-heap',
        label: t('device_health_modal.metrics.free_heap'),
        measureName: 'freeheap',
        unit: 'MB',
        color: '#5c6bc0',
        transform: (value: number) => value / BYTES_IN_MB,
        formatValue: (value: number) => `${value.toFixed(2)} MB`
    }
]);

const buildRebootMetrics = (t: (key: string) => string): TelemetryMetricConfig[] => ([
    {
        id: 'uptime',
        label: t('device_health_modal.metrics.up_time'),
        measureName: 'uptime',
        color: '#ec407a',
        transform: (value: number) => value / SECONDS_IN_HOUR,
        formatValue: (hours: number) => formatUptimeLabel(hours)
    }
]);

const buildWifiMetrics = (t: (key: string) => string): TelemetryMetricConfig[] => ([
    {
        id: 'wifi-rssi',
        label: t('device_health_modal.metrics.wifi_rssi'),
        measureName: 'wifi_rssi',
        color: WIFI_RSSI_COLORS.line,
        unit: '%',
        transform: (value: number) => getWifiRssiQualityPercent(value),
        formatValue: (value: number) => `${value.toFixed(0)}%`
    }
]);

const timeFormatter = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });

function formatUptimeLabel(hours: number) {
    const days = hours / 24;
    const weeks = days / 7;

    if (weeks >= 1) {
        return `${weeks.toFixed(2)} w`;
    }

    if (days >= 1) {
        return `${days.toFixed(2)} d`;
    }

    return `${hours.toFixed(1)} h`;
}

const normalizeMetricKey = (value?: string) => {
    return value?.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() ?? '';
};

const parseMetricTimestamp = (time: string | number | undefined): Date | undefined => {
    if (time === undefined || time === null) {
        return undefined;
    }

    const date = new Date(time);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

const formatAxisTimestamp = (value: Date | number) => {
    if (value instanceof Date) {
        return timeFormatter.format(value);
    }

    const dateValue = new Date(value);
    return Number.isNaN(dateValue.getTime()) ? '' : timeFormatter.format(dateValue);
};

const matchMetricsForConfig = (
    config: TelemetryMetricConfig,
    metricsByKey: Map<string, NormalizedMetricPoint[]>
) => {
    const normalizedName = normalizeMetricKey(config.measureName);
    if (!normalizedName) {
        return undefined;
    }

    const directMatch = metricsByKey.get(normalizedName);
    if (directMatch?.length) {
        return directMatch;
    }

    for (const [key, points] of metricsByKey.entries()) {
        if (key.includes(normalizedName) && points?.length) {
            return points;
        }
    }

    return undefined;
};

const getWifiRssiQuality = (value: number, t: (key: string) => string) => {
    if (value >= WIFI_RSSI_THRESHOLDS.excellent) {
        return { label: t('device_health_modal.wifi_quality.excellent'), color: WIFI_RSSI_COLORS.excellent };
    }

    if (value >= WIFI_RSSI_THRESHOLDS.good) {
        return { label: t('device_health_modal.wifi_quality.good'), color: WIFI_RSSI_COLORS.good };
    }

    if (value >= WIFI_RSSI_THRESHOLDS.medium) {
        return { label: t('device_health_modal.wifi_quality.medium'), color: WIFI_RSSI_COLORS.medium };
    }

    return { label: t('device_health_modal.wifi_quality.poor'), color: WIFI_RSSI_COLORS.poor };
};

const getWifiRssiQualityPercent = (value: number) => {
    const min = WIFI_RSSI_THRESHOLDS.poor;
    const max = WIFI_RSSI_THRESHOLDS.excellent;
    const clamped = Math.min(Math.max(value, min), max);
    return Math.round(((clamped - min) / (max - min)) * 100);
};

const getWifiRssiQualityStop = (value: number) => {
    return `${100 - getWifiRssiQualityPercent(value)}%`;
};

const buildChartSeries = (
    configs: TelemetryMetricConfig[],
    metricsByKey: Map<string, NormalizedMetricPoint[]>
): TelemetryChartSeries[] => {
    return configs.map(config => {
        const matchedPoints = matchMetricsForConfig(config, metricsByKey) ?? [];

        const data = matchedPoints.map(point => {
            const transformedValue = config.transform ? config.transform(point.value) : point.value;
            return Number.isFinite(transformedValue) ? transformedValue : point.value;
        });

        return {
            id: config.id,
            label: config.label,
            color: config.color,
            unit: config.unit,
            formatValue: config.formatValue,
            data,
            timeline: matchedPoints.map(point => point.timestamp)
        };
    });
};

interface DeviceHealthModalProps {
    dismiss: (data?: string | null | undefined | number, role?: string) => void;
    deviceId: string;
}

const DeviceHealthModal: React.FC<DeviceHealthModalProps> = ({
    dismiss,
    deviceId
}) => {
    const { t } = useTranslation('components');
    const deviceQuery = useDeviceQuery(deviceId);
    const deviceRebootLogsQuery = useDeviceRebootLogsQuery(deviceId);
    const telemetryMetricsQuery = useDeviceTelemetryMetricsQuery(deviceId);
    const developerModeQuery = useDeveloperModeQuery();
    const isDeveloperMode = !!developerModeQuery.data;
    const isRefreshing = deviceQuery.isLoading
        || deviceQuery.isFetching
        || deviceRebootLogsQuery.isLoading
        || deviceRebootLogsQuery.isFetching
        || telemetryMetricsQuery.isLoading
        || telemetryMetricsQuery.isFetching
        || developerModeQuery.isLoading
        || developerModeQuery.isFetching;
    const handleRefresh = async () => {
        await Promise.all([
            deviceQuery.refetch(),
            deviceRebootLogsQuery.refetch(),
            telemetryMetricsQuery.refetch(),
            developerModeQuery.refetch()
        ]);
    };
    const device = useMemo(
        () => (deviceQuery.data ? new DeviceModel(deviceQuery.data) : undefined),
        [deviceQuery.data]
    );
    const connectivity = deviceQuery.data?.connectivity;
    const connectionStatus = connectivity?.connected;
    const statusColor = connectionStatus === undefined
        ? 'medium'
        : connectionStatus
            ? 'success'
            : 'danger';
    const statusLabel = connectionStatus === undefined
        ? t('device_event_item.unknown')
        : connectionStatus
            ? t('device_list_item.online')
            : t('device_list_item.offline');
    const statusIcon = connectionStatus === false ? unlinkOutline : wifi;
    const lastEventLabel = connectionStatus === false
        ? t('device_health_modal.last_disconnected')
        : t('device_health_modal.last_connected');
    const notAvailableLabel = t('device_health_modal.not_available');
    const disconnectReason = connectivity?.disconnectReason;
    const timestamp = connectivity?.timestamp;
    const macId = device?.macId ?? notAvailableLabel;
    const deviceName = device?.description ?? notAvailableLabel;
    const magicWords = device?.humanHash ?? notAvailableLabel;
    const latestBuild = useMemo(() => {
        const buildEvents = telemetryMetricsQuery.data?.filter(metric => {
            return normalizeMetricKey(metric.measureName) === 'build';
        });

        if (!buildEvents?.length) {
            return undefined;
        }

        const latestEvent = buildEvents.reduce<{
            timestamp: Date;
            value: string | number;
        } | undefined>((current, metric) => {
            const metricTimestamp = parseMetricTimestamp(metric.time);
            if (!metricTimestamp) {
                return current;
            }

            if (!current || metricTimestamp > current.timestamp) {
                return { timestamp: metricTimestamp, value: metric.value };
            }

            return current;
        }, undefined);

        return latestEvent?.value;
    }, [telemetryMetricsQuery.data]);
    const versionLabel = latestBuild === undefined || latestBuild === ''
        ? notAvailableLabel
        : String(latestBuild);
    const metricsByKey = useMemo(() => {
        const map = new Map<string, NormalizedMetricPoint[]>();

        telemetryMetricsQuery.data?.forEach(metric => {
            if (!metric?.measureName) {
                return;
            }

            const normalizedKey = normalizeMetricKey(metric.measureName);
            if (!normalizedKey) {
                return;
            }

            const numericValue = typeof metric.value === 'number'
                ? metric.value
                : Number(metric.value);

            if (!Number.isFinite(numericValue)) {
                return;
            }

            const timestamp = parseMetricTimestamp(metric.time);
            if (!timestamp) {
                return;
            }
            const existingPoints = map.get(normalizedKey) ?? [];

            existingPoints.push({
                timestamp,
                value: numericValue
            });

            map.set(normalizedKey, existingPoints);
        });

        map.forEach(points => {
            points.sort((a, b) => {
                return a.timestamp.getTime() - b.timestamp.getTime();
            });
        });

        return map;
    }, [telemetryMetricsQuery.data]);

    const temperatureMetrics = useMemo(() => buildTemperatureMetrics(t), [t]);
    const powerMetrics = useMemo(() => buildPowerMetrics(t), [t]);
    const storageMetrics = useMemo(() => buildStorageMetrics(t), [t]);
    const rebootMetrics = useMemo(() => buildRebootMetrics(t), [t]);
    const wifiMetrics = useMemo(() => buildWifiMetrics(t), [t]);
    const temperatureSeries = useMemo(
        () => buildChartSeries(temperatureMetrics, metricsByKey),
        [metricsByKey, temperatureMetrics]
    );
    const powerSeries = useMemo(
        () => buildChartSeries(powerMetrics, metricsByKey),
        [metricsByKey, powerMetrics]
    );
    const storageSeries = useMemo(
        () => buildChartSeries(storageMetrics, metricsByKey),
        [metricsByKey, storageMetrics]
    );
    const rebootSeries = useMemo(
        () => buildChartSeries(rebootMetrics, metricsByKey),
        [metricsByKey, rebootMetrics]
    );
    const wifiSeries = useMemo(
        () => buildChartSeries(wifiMetrics, metricsByKey),
        [metricsByKey, wifiMetrics]
    );
    const wifiRawPoints = useMemo(() => {
        const config = wifiMetrics[0];
        if (!config) {
            return [];
        }

        return matchMetricsForConfig(config, metricsByKey) ?? [];
    }, [metricsByKey, wifiMetrics]);
    const primaryColor = '#008FD5';
    const rebootLogs = deviceRebootLogsQuery.data ?? [];
    const chartWidth = typeof window === 'undefined'
        ? 360
        : Math.max(280, Math.min(window.innerWidth - 120, 420));

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>{t('device_health_modal.title')}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton
                            fill="clear"
                            title="Refresh"
                            onClick={handleRefresh}
                            disabled={isRefreshing}
                        >
                            <IonIcon slot="icon-only" icon={refreshOutline} />
                        </IonButton>
                        <IonButton
                            fill="clear"
                            onClick={() => dismiss(null, 'cancel')}
                        >
                            <IonIcon slot="icon-only" icon={closeOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
                {isRefreshing && <IonProgressBar type="indeterminate" />}
            </IonHeader>
            <IonContent color="light">
                <SectionHeader>{t('device_health_modal.device_status')}</SectionHeader>
                <IonList inset={true} className={sectionStyles.insetList}>
                    <IonItem>
                        <IonLabel>
                            <h2>{t('device_health_modal.status')}</h2>
                        </IonLabel>
                        <IonChip
                            color={statusColor}
                            slot="end">
                            <IonIcon icon={statusIcon} />
                            <IonLabel>{statusLabel}</IonLabel>
                        </IonChip>
                    </IonItem>
                    <IonItem>
                        <IonLabel>
                            <h2>{lastEventLabel}</h2>
                            <p>{timestamp ? <Timestamp timestamp={timestamp} /> : notAvailableLabel}</p>
                        </IonLabel>
                    </IonItem>
                    <IonItem>
                        <IonLabel>
                            <h2>{t('device_health_modal.version')}</h2>
                            <p>{versionLabel}</p>
                        </IonLabel>
                    </IonItem>
                    {connectionStatus === false && disconnectReason && (
                        <IonItem>
                            <IonLabel>
                                <h2>{t('device_health_modal.disconnect_reason')}</h2>
                                <p>{disconnectReason}</p>
                            </IonLabel>
                        </IonItem>
                    )}
                </IonList>
                <SectionHeader>{t('device_health_modal.device_details')}</SectionHeader>
                <IonList inset={true} className={sectionStyles.insetList}>
                    <IonItem>
                        <IonLabel>
                            <h2>{t('device_health_modal.product_name')}</h2>
                            <p>OnlyCat</p>
                        </IonLabel>
                    </IonItem>
                    <IonItem>
                        <IonLabel>
                            <h2>{t('device_health_modal.mac_id')}</h2>
                            <p>{macId}</p>
                        </IonLabel>
                    </IonItem>
                    <IonItem>
                        <IonLabel>
                            <h2>{t('device_health_modal.device_name')}</h2>
                            <p>{deviceName}</p>
                        </IonLabel>
                    </IonItem>
                    <IonItem>
                        <IonLabel>
                            <h2>{t('device_health_modal.magic_words')}</h2>
                            <p>{magicWords}</p>
                        </IonLabel>
                    </IonItem>
                </IonList>
                <SectionHeader>{t('device_health_modal.wifi_signal')}</SectionHeader>
                <IonList inset={true} className={sectionStyles.insetList}>
                    {wifiSeries.map(series => {
                        const seriesColor = series.color ?? primaryColor;
                        const timeline = series.timeline;
                        const gradientId = `${series.id}-fill-gradient`;
                        const latestRawValue = wifiRawPoints.length
                            ? wifiRawPoints[wifiRawPoints.length - 1].value
                            : undefined;
                        const showMarks = series.data.length <= 1;
                        const latestPercent = latestRawValue !== undefined
                            ? getWifiRssiQualityPercent(latestRawValue)
                            : undefined;
                        const formatValue = (value: number) => {
                            const baseLabel = series.formatValue?.(value)
                                ?? `${value.toFixed(0)} ${series.unit ?? ''}`.trim();
                            return baseLabel;
                        };
                        const quality = latestRawValue !== undefined
                            ? getWifiRssiQuality(latestRawValue, t)
                            : undefined;

                        return (
                            <IonItem key={series.id} lines="none">
                                <div style={{ width: '100%', padding: '12px 0' }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            justifyContent: 'space-between',
                                            marginBottom: '8px'
                                        }}
                                    >
                                        <span style={{ fontWeight: 600 }}>{series.label}</span>
                                        <span
                                            style={{
                                                fontSize: '14px',
                                                color: quality?.color ?? '#666',
                                                fontWeight: quality ? 600 : 400
                                            }}
                                        >
                                            {latestRawValue !== undefined
                                                ? `${latestRawValue.toFixed(0)} dBm · ${latestPercent}%`
                                                : notAvailableLabel}
                                            {quality ? ` · ${quality.label}` : ''}
                                        </span>
                                    </div>
                                    <svg width="0" height="0" aria-hidden="true" style={{ position: 'absolute' }}>
                                        <defs>
                                            <linearGradient
                                                id={gradientId}
                                                x1="0"
                                                y1={WIFI_CHART_MARGIN.top}
                                                x2="0"
                                                y2={WIFI_CHART_HEIGHT - WIFI_CHART_MARGIN.bottom}
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop
                                                    offset={getWifiRssiQualityStop(WIFI_RSSI_THRESHOLDS.excellent)}
                                                    stopColor={WIFI_RSSI_COLORS.excellent}
                                                    stopOpacity={AREA_GRADIENT_TOP_OPACITY}
                                                />
                                                <stop
                                                    offset={getWifiRssiQualityStop(WIFI_RSSI_THRESHOLDS.good)}
                                                    stopColor={WIFI_RSSI_COLORS.good}
                                                    stopOpacity={AREA_GRADIENT_TOP_OPACITY}
                                                />
                                                <stop
                                                    offset={getWifiRssiQualityStop(WIFI_RSSI_THRESHOLDS.medium)}
                                                    stopColor={WIFI_RSSI_COLORS.medium}
                                                    stopOpacity={AREA_GRADIENT_TOP_OPACITY}
                                                />
                                                <stop
                                                    offset={getWifiRssiQualityStop(WIFI_RSSI_THRESHOLDS.poor)}
                                                    stopColor={WIFI_RSSI_COLORS.poor}
                                                    stopOpacity={AREA_GRADIENT_TOP_OPACITY}
                                                />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                    <LineChart
                                        width={chartWidth}
                                        height={WIFI_CHART_HEIGHT}
                                        margin={WIFI_CHART_MARGIN}
                                        hideLegend
                                        axisHighlight={{ x: 'line', y: 'none' }}
                                        series={[{
                                            id: `${series.id}-line`,
                                            data: series.data,
                                            color: seriesColor,
                                            area: true,
                                            curve: 'monotoneX',
                                            showMark: showMarks,
                                            valueFormatter: (value: number | null) => (
                                                value === null || value === undefined
                                                    ? ''
                                                    : formatValue(value)
                                            )
                                        }]}
                                        xAxis={[{
                                            id: `${series.id}-time`,
                                            scaleType: 'time',
                                            data: timeline,
                                            valueFormatter: value => formatAxisTimestamp(value as Date | number)
                                        }]}
                                        yAxis={[{
                                            id: `${series.id}-rssi`,
                                            min: 0,
                                            max: 100,
                                            valueFormatter: (value: number | null) => (
                                                value === null || value === undefined
                                                    ? ''
                                                    : `${value.toFixed(0)}%`
                                            )
                                        }]}
                                        slotProps={{
                                            area: {
                                                style: { fill: `url(#${gradientId})` }
                                            }
                                        }}
                                    />
                                </div>
                            </IonItem>
                        );
                    })}
                </IonList>
                {isDeveloperMode && (
                    <>
                    <SectionHeader>{t('device_health_modal.temperature_trends')}</SectionHeader>
                    <IonList inset={true} className={sectionStyles.insetList}>
                        {temperatureSeries.map(series => {
                            const seriesColor = series.color ?? primaryColor;
                            const timeline = series.timeline;
                            const gradientId = `${series.id}-fill-gradient`;
                            const latestValue = series.data.length
                                ? series.data[series.data.length - 1]
                                : undefined;
                            const showMarks = series.data.length <= 1;
                            const formatValue = (value: number) => (
                                series.formatValue?.(value)
                                ?? `${value.toFixed(1)}${series.unit ?? ''}`
                            );
                            return (
                                <IonItem key={series.id} lines="none">
                                    <div style={{ width: '100%', padding: '12px 0' }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                justifyContent: 'space-between',
                                                marginBottom: '8px'
                                            }}
                                        >
                                            <span style={{ fontWeight: 600 }}>{series.label}</span>
                                            <span style={{ fontSize: '14px', color: '#666' }}>
                                                {latestValue !== undefined
                                                    ? formatValue(latestValue)
                                                    : notAvailableLabel}
                                            </span>
                                        </div>
                                        <svg width="0" height="0" aria-hidden="true" style={{ position: 'absolute' }}>
                                            <defs>
                                                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor={seriesColor} stopOpacity={AREA_GRADIENT_TOP_OPACITY} />
                                                    <stop offset="100%" stopColor={seriesColor} stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                        </svg>
                                        <LineChart
                                            width={chartWidth}
                                            height={120}
                                            margin={{ top: 12, bottom: 8, left: 4, right: 4 }}
                                            hideLegend
                                            axisHighlight={{ x: 'line', y: 'none' }}
                                            series={[{
                                                id: `${series.id}-line`,
                                                data: series.data,
                                                color: seriesColor,
                                                area: true,
                                                curve: 'monotoneX',
                                                showMark: showMarks,
                                                valueFormatter: (value: number | null) => (
                                                    value === null || value === undefined
                                                        ? ''
                                                        : formatValue(value)
                                                )
                                            }]}
                                            xAxis={[{
                                                id: `${series.id}-time`,
                                                scaleType: 'time',
                                                data: timeline,
                                                valueFormatter: value => formatAxisTimestamp(value as Date | number)
                                            }]}
                                            slotProps={{
                                                area: {
                                                    style: { fill: `url(#${gradientId})` }
                                                }
                                            }}
                                        />
                                    </div>
                                </IonItem>
                            );
                        })}
                    </IonList>
                    </>
                )}
                {isDeveloperMode && (
                    <>
                    <SectionHeader>{t('device_health_modal.power_management')}</SectionHeader>
                    <IonList inset={true} className={sectionStyles.insetList}>
                        {powerSeries.map(series => {
                            const seriesColor = series.color ?? primaryColor;
                            const timeline = series.timeline;
                            const gradientId = `${series.id}-fill-gradient`;
                            const latestValue = series.data.length
                                ? series.data[series.data.length - 1]
                                : undefined;
                            const showMarks = series.data.length <= 1;
                            const formatValue = (value: number) => {
                                if (series.formatValue) {
                                    return series.formatValue(value);
                                }

                                const baseValue = value >= 10 ? value.toFixed(0) : value.toFixed(2);
                                return series.unit ? `${baseValue} ${series.unit}` : baseValue;
                            };
                            return (
                                <IonItem key={series.id} lines="none">
                                    <div style={{ width: '100%', padding: '12px 0' }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                justifyContent: 'space-between',
                                                marginBottom: '8px'
                                            }}
                                        >
                                            <span style={{ fontWeight: 600 }}>{series.label}</span>
                                            <span style={{ fontSize: '14px', color: '#666' }}>
                                                {latestValue !== undefined
                                                    ? formatValue(latestValue)
                                                    : notAvailableLabel}
                                            </span>
                                        </div>
                                        <svg width="0" height="0" aria-hidden="true" style={{ position: 'absolute' }}>
                                            <defs>
                                                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor={seriesColor} stopOpacity={AREA_GRADIENT_TOP_OPACITY} />
                                                    <stop offset="100%" stopColor={seriesColor} stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                        </svg>
                                        <LineChart
                                            width={chartWidth}
                                            height={120}
                                            margin={{ top: 12, bottom: 8, left: 4, right: 4 }}
                                            hideLegend
                                            axisHighlight={{ x: 'line', y: 'none' }}
                                            series={[{
                                                id: `${series.id}-line`,
                                                data: series.data,
                                                color: seriesColor,
                                                area: true,
                                                curve: 'monotoneX',
                                                showMark: showMarks,
                                                valueFormatter: (value: number | null) => (
                                                    value === null || value === undefined
                                                        ? ''
                                                        : formatValue(value)
                                                )
                                            }]}
                                            xAxis={[{
                                                id: `${series.id}-time`,
                                                scaleType: 'time',
                                                data: timeline,
                                                valueFormatter: value => formatAxisTimestamp(value as Date | number)
                                            }]}
                                            slotProps={{
                                                area: {
                                                    style: { fill: `url(#${gradientId})` }
                                                }
                                            }}
                                        />
                                    </div>
                                </IonItem>
                            );
                        })}
                    </IonList>
                    </>
                )}
                {isDeveloperMode && (
                    <>
                    <SectionHeader>{t('device_health_modal.storage')}</SectionHeader>
                    <IonList inset={true} className={sectionStyles.insetList}>
                        {storageSeries.map(series => {
                            const seriesColor = series.color ?? primaryColor;
                            const timeline = series.timeline;
                            const gradientId = `${series.id}-fill-gradient`;
                            const latestValue = series.data.length
                                ? series.data[series.data.length - 1]
                                : undefined;
                            const showMarks = series.data.length <= 1;
                            const unitLabel = series.unit ?? '';
                            const formatValue = (value: number) => (
                                series.formatValue?.(value)
                                ?? `${value.toFixed(2)} ${unitLabel}`.trim()
                            );
                            return (
                                <IonItem key={series.id} lines="none">
                                    <div style={{ width: '100%', padding: '12px 0' }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                justifyContent: 'space-between',
                                                marginBottom: '8px'
                                            }}
                                        >
                                            <span style={{ fontWeight: 600 }}>{series.label}</span>
                                            <span style={{ fontSize: '14px', color: '#666' }}>
                                                {latestValue !== undefined
                                                    ? formatValue(latestValue)
                                                    : notAvailableLabel}
                                            </span>
                                        </div>
                                        <svg width="0" height="0" aria-hidden="true" style={{ position: 'absolute' }}>
                                            <defs>
                                                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor={seriesColor} stopOpacity={AREA_GRADIENT_TOP_OPACITY} />
                                                    <stop offset="100%" stopColor={seriesColor} stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                        </svg>
                                        <LineChart
                                            width={chartWidth}
                                            height={120}
                                            margin={{ top: 12, bottom: 8, left: 4, right: 4 }}
                                            hideLegend
                                            axisHighlight={{ x: 'line', y: 'none' }}
                                            series={[{
                                                id: `${series.id}-line`,
                                                data: series.data,
                                                color: seriesColor,
                                                area: true,
                                                curve: 'monotoneX',
                                                showMark: showMarks,
                                                valueFormatter: (value: number | null) => (
                                                    value === null || value === undefined
                                                        ? ''
                                                        : formatValue(value)
                                                )
                                            }]}
                                            xAxis={[{
                                                id: `${series.id}-time`,
                                                scaleType: 'time',
                                                data: timeline,
                                                valueFormatter: value => formatAxisTimestamp(value as Date | number)
                                            }]}
                                            slotProps={{
                                                area: {
                                                    style: { fill: `url(#${gradientId})` }
                                                }
                                            }}
                                        />
                                    </div>
                                </IonItem>
                            );
                        })}
                    </IonList>
                    </>
                )}
                <SectionHeader>{t('device_health_modal.reboots')}</SectionHeader>
                <IonList inset={true} className={sectionStyles.insetList}>
                    {rebootSeries.map(series => {
                        const seriesColor = series.color ?? primaryColor;
                        const timeline = series.timeline;
                        const gradientId = `${series.id}-fill-gradient`;
                        const latestValue = series.data.length
                            ? series.data[series.data.length - 1]
                            : undefined;
                        const showMarks = series.data.length <= 1;
                        const unitLabel = series.unit ?? '';
                        const formatValue = (value: number) => (
                            series.formatValue?.(value)
                            ?? `${value.toFixed(0)} ${unitLabel}`.trim()
                        );
                        return (
                            <IonItem key={series.id} lines="none">
                                <div style={{ width: '100%', padding: '12px 0' }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            justifyContent: 'space-between',
                                            marginBottom: '8px'
                                        }}
                                    >
                                        <span style={{ fontWeight: 600 }}>{series.label}</span>
                                        <span style={{ fontSize: '14px', color: '#666' }}>
                                            {latestValue !== undefined
                                                ? formatValue(latestValue)
                                                : notAvailableLabel}
                                        </span>
                                    </div>
                                    <svg width="0" height="0" aria-hidden="true" style={{ position: 'absolute' }}>
                                        <defs>
                                            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={seriesColor} stopOpacity={AREA_GRADIENT_TOP_OPACITY} />
                                                <stop offset="100%" stopColor={seriesColor} stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                    <LineChart
                                        width={chartWidth}
                                        height={120}
                                        margin={{ top: 12, bottom: 8, left: 4, right: 4 }}
                                        hideLegend
                                        axisHighlight={{ x: 'line', y: 'none' }}
                                        series={[{
                                            id: `${series.id}-line`,
                                            data: series.data,
                                            color: seriesColor,
                                            area: true,
                                            curve: 'monotoneX',
                                            showMark: showMarks,
                                            valueFormatter: (value: number | null) => (
                                                value === null || value === undefined
                                                    ? ''
                                                    : formatValue(value)
                                            )
                                        }]}
                                        xAxis={[{
                                            id: `${series.id}-time`,
                                            scaleType: 'time',
                                            data: timeline,
                                            valueFormatter: value => formatAxisTimestamp(value as Date | number)
                                        }]}
                                        slotProps={{
                                            area: {
                                                style: { fill: `url(#${gradientId})` }
                                            }
                                        }}
                                    />
                                </div>
                            </IonItem>
                        );
                    })}
                </IonList>
                <SectionHeader>{t('device_health_modal.error_logs')}</SectionHeader>
                <IonList inset={true} className={sectionStyles.insetList}>
                    {rebootLogs.length ? (
                        rebootLogs.map((entry, index) => {
                            const logTimestamp = entry.timestamp;
                            const messageLabel = entry.message ?? t('device_health_modal.no_message');
                            return (
                                <IonItem key={`${entry.timestamp?.toISOString() ?? 'unknown'}-${index}`}>
                                    <IonLabel>
                                        <h2>{messageLabel}</h2>
                                        <p>{logTimestamp ? <Timestamp timestamp={logTimestamp} /> : notAvailableLabel}</p>
                                    </IonLabel>
                                </IonItem>
                            );
                        })
                    ) : (
                        <IonItem lines="full">
                            <IonLabel>
                                <p>{t('device_health_modal.no_recent_error_logs')}</p>
                            </IonLabel>
                        </IonItem>
                    )}
                </IonList>
            </IonContent>
        </IonPage>
    );
};

export default DeviceHealthModal;
